import { useServicesContext } from "@/modules/core/services/services.context"
import { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import {
  UpdateSessionInputDtoSchema,
  type UpdateSessionInputDto,
} from "@/modules/sessions/gateway/programInfiniteSessionDto"
import { TemplateElementEntityFactory } from "@/modules/template/entities/templateElementEntity"
import { TemplateEntity } from "@/modules/template/entities/templateEntity"
import {
  ApplySessionTemplateInputDtoSchema,
  ApplyWeekTemplateInputDtoSchema,
  type ApplyExerciseTemplateInputDto,
  type ApplySessionTemplateInputDto,
  type ApplyWeekTemplateInputDto,
  type CreateExerciseTemplateInputDto,
  type CreateSessionTemplateInputDto,
  type CreateWeekTemplateInputDto,
  type EditExerciseTemplateInputDto,
  type EditTemplateInputDto,
} from "@/modules/template/gateway/templateDto"
import { templateKeys } from "@/modules/template/gateway/templateKeys"
import { TemplateTypeFilter } from "@/modules/template/types/templateType"
import { DEFAULT_PAGE_SIZE } from "@/modules/utils/paginated"
import { useMutation, useQueryClient, useSuspenseInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query"
import { useMemo, useState } from "react"

export function useTemplatesTable({
  templateTypeFilter,
  defaultPage,
  defaultPageSize,
  searchTerm,
}: {
  templateTypeFilter: TemplateTypeFilter
  defaultPage?: number
  defaultPageSize?: number
  searchTerm?: string
}) {
  const { templateApi } = useServicesContext()
  const [page, setPage] = useState(defaultPage ?? 0)
  const [pageSize, setPageSize] = useState(defaultPageSize ?? DEFAULT_PAGE_SIZE)

  const { data, isLoading, error } = useSuspenseQuery({
    queryKey: templateKeys.tableList(templateTypeFilter, { page, pageSize, searchTerm }),
    queryFn: async () => {
      const result = await templateApi.getTemplates(templateTypeFilter, searchTerm, page, pageSize)
      return { ...result, content: result.content.map((item) => TemplateEntity.fromDto(item)) }
    },
  })

  const handleSetPage = (newPageNumber: number) => {
    setPage(newPageNumber)
  }

  const handlePreviousPage = () => {
    if (data.page > 0) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (data.page < data.totalPages - 1) {
      setPage((prev) => prev + 1)
    }
  }

  return {
    data: data.content,
    isLoading,
    error,
    pageSize,
    setPageSize,
    handleSetPage,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage: data.page > 0,
    hasNextPage: data.page < data.totalPages - 1,
    totalElements: data.totalElements,
  }
}

export function useTemplatesInfinite({
  templateTypeFilter,
  searchTerm,
  page = 0,
  pageSize = DEFAULT_PAGE_SIZE,
}: {
  templateTypeFilter: TemplateTypeFilter
  searchTerm?: string
  page?: number
  pageSize?: number
}) {
  const { templateApi } = useServicesContext()
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery({
    queryKey: templateKeys.infiniteList(templateTypeFilter, { page, pageSize, searchTerm }),
    queryFn: async () => {
      const result = await templateApi.getTemplates(templateTypeFilter, searchTerm, page, pageSize)
      return { ...result, content: result.content.map((item) => TemplateEntity.fromDto(item)) }
    },
    initialPageParam: page,
    getNextPageParam: (lastPage) => {
      if (lastPage.content.length === 0) {
        return null
      }
      return lastPage.page + 1
    },
  })

  const templates = useMemo(() => data.pages.flatMap((dataPage) => dataPage.content), [data])

  return {
    data: templates,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
}

/**
 * Suspense query that fetches a single template by id.
 * @param templateId - Template identifier.
 */
export function useTemplate(templateId: string) {
  const { templateApi } = useServicesContext()
  return useSuspenseQuery({
    queryKey: templateKeys.item(templateId),
    queryFn: async () => {
      const result = await templateApi.getTemplate(templateId)
      return TemplateEntity.fromDto(result)
    },
  })
}

/**
 * Suspense query that fetches a template's element payload (structure/content) by template id.
 * @param templateId - Template identifier.
 */
export function useTemplateElement(templateId: string) {
  const { templateApi } = useServicesContext()
  return useSuspenseQuery({
    queryKey: templateKeys.element(templateId),
    queryFn: async () => {
      const result = await templateApi.getTemplateElement(templateId)
      return TemplateElementEntityFactory.fromDto(result)
    },
  })
}

/**
 * Create exercise template
 * Creates an exercise template and invalidates exercise template list queries.
 */
export function useCreateExerciseTemplate() {
  const { templateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (createTemplateInput: CreateExerciseTemplateInputDto) => {
      await templateApi.createExerciseTemplate(createTemplateInput)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.tableList(TemplateTypeFilter.EXERCISE) })
      void queryClient.invalidateQueries({ queryKey: templateKeys.infiniteList(TemplateTypeFilter.EXERCISE) })
    },
  })
}

/**
 * Create session template
 * Creates a session template and invalidates session template list queries.
 */
export function useCreateSessionTemplate() {
  const { templateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (createTemplateInput: CreateSessionTemplateInputDto) => {
      await templateApi.createSessionTemplate(createTemplateInput)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.tableList(TemplateTypeFilter.SESSION) })
      void queryClient.invalidateQueries({ queryKey: templateKeys.infiniteList(TemplateTypeFilter.SESSION) })
    },
  })
}

/**
 * Create week template
 * Creates a week template and invalidates week and `TemplateTypeFilter.ALL` list queries.
 */
export function useCreateWeekTemplate() {
  const { templateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (createTemplateInput: CreateWeekTemplateInputDto) => {
      await templateApi.createWeekTemplate(createTemplateInput)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.tableList(TemplateTypeFilter.WEEK) })
      void queryClient.invalidateQueries({ queryKey: templateKeys.infiniteList(TemplateTypeFilter.WEEK) })
      void queryClient.invalidateQueries({ queryKey: templateKeys.tableList(TemplateTypeFilter.ALL) })
      void queryClient.invalidateQueries({ queryKey: templateKeys.infiniteList(TemplateTypeFilter.ALL) })
    },
  })
}

/**
 * Apply exercise template
 * Applies an exercise template to a target session block; invalidates program and training session queries.
 */
export function useApplyExerciseTemplate() {
  const { templateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      templateId,
      applyExerciseTemplateInput,
    }: {
      templateId: string
      applyExerciseTemplateInput: ApplyExerciseTemplateInputDto
    }) => {
      const result = await templateApi.applyExerciseTemplate(templateId, applyExerciseTemplateInput)
      return SessionBlockExerciseEntity.fromDto(result)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["programSession"] })
      void queryClient.invalidateQueries({ queryKey: ["programSessions"] })
      void queryClient.invalidateQueries({ queryKey: ["trainingSessions"] })
    },
  })
}

/**
 * Apply session template
 * Applies a session template after validating input with `ApplySessionTemplateInputDtoSchema`.
 */
export function useApplySessionTemplate() {
  const { templateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      templateId,
      applySessionTemplateInput,
    }: {
      templateId: string
      applySessionTemplateInput: ApplySessionTemplateInputDto
    }) => {
      const parseResult = ApplySessionTemplateInputDtoSchema.safeParse(applySessionTemplateInput)
      if (!parseResult.success) {
        throw new Error("applySessionTemplateInput is invalid: " + parseResult.error.message)
      }
      await templateApi.applySessionTemplate(templateId, applySessionTemplateInput)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["programSession"] })
      void queryClient.invalidateQueries({ queryKey: ["programSessions"] })
      void queryClient.invalidateQueries({ queryKey: ["trainingSessions"] })
    },
  })
}

/**
 * Apply week template
 * Applies a week template after validating input with `ApplyWeekTemplateInputDtoSchema`.
 */
export function useApplyWeekTemplate() {
  const { templateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      templateId,
      applyWeekTemplateInput,
    }: {
      templateId: string
      applyWeekTemplateInput: ApplyWeekTemplateInputDto
    }) => {
      const parseResult = ApplyWeekTemplateInputDtoSchema.safeParse(applyWeekTemplateInput)
      if (!parseResult.success) {
        throw new Error("applyWeekTemplateInput is invalid: " + parseResult.error.message)
      }
      await templateApi.applyWeekTemplate(templateId, applyWeekTemplateInput)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["programSession"] })
      void queryClient.invalidateQueries({ queryKey: ["programSessions"] })
      void queryClient.invalidateQueries({ queryKey: ["trainingSessions"] })
    },
  })
}

/**
 * Edit template
 * Updates template metadata (name, description, categories) and refreshes list and item cache.
 */
export function useEditTemplate() {
  const { templateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ templateId, template }: { templateId: string; template: EditTemplateInputDto }) => {
      return await templateApi.editTemplate(templateId, template)
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.tableList(data.templateType) })
      void queryClient.invalidateQueries({ queryKey: templateKeys.infiniteList(data.templateType) })
      void queryClient.setQueryData(templateKeys.item(data.id), TemplateEntity.fromDto(data))
    },
  })
}

/**
 * Edit exercise template
 * Updates an exercise template and invalidates the template element query for that template.
 */
export function useEditExerciseTemplate() {
  const { templateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      templateId,
      exerciseInput,
    }: {
      templateId: string
      exerciseInput: EditExerciseTemplateInputDto
    }) => {
      await templateApi.editExerciseTemplate(templateId, exerciseInput)
    },
    onSuccess: (_, { templateId }) => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.element(templateId) })
    },
  })
}

/**
 * Create session in template
 * Adds a session to a week template; validates `sessionInput` with `UpdateSessionInputDtoSchema`.
 */
export function useCreateSessionInTemplate() {
  const { templateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ templateId, sessionInput }: { templateId: string; sessionInput: UpdateSessionInputDto }) => {
      const parseResult = UpdateSessionInputDtoSchema.safeParse(sessionInput)
      if (!parseResult.success) {
        throw new Error("sessionInput is invalid: " + parseResult.error.message)
      }
      return await templateApi.createSessionInTemplate(templateId, sessionInput)
    },
    onSuccess: (_, { templateId }) => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.element(templateId) })
    },
  })
}

/**
 * Edit session in template
 * Updates a session inside a week template and invalidates the template element query.
 */
export function useEditSessionInTemplate() {
  const { templateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      templateId,
      sessionId,
      sessionInput,
    }: {
      templateId: string
      sessionId: string
      sessionInput: UpdateSessionInputDto
    }) => {
      await templateApi.editSessionInTemplate(templateId, sessionId, sessionInput)
    },
    onSuccess: (_, { templateId }) => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.element(templateId) })
    },
  })
}

/**
 * Delete template
 * Deletes a template by id and invalidates table, infinite list, and item queries.
 */
export function useDeleteTemplate() {
  const { templateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (templateId: string) => {
      await templateApi.deleteTemplate(templateId)
    },
    onSuccess: (_, templateId) => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.tableList() })
      void queryClient.invalidateQueries({ queryKey: templateKeys.infiniteList() })
      void queryClient.invalidateQueries({ queryKey: templateKeys.item(templateId) })
    },
  })
}

/**
 * Delete session template
 * Removes a session from a session-type template and invalidates session template list queries.
 */
export function useDeleteSessionTemplate() {
  const { templateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ templateId, sessionId }: { templateId: string; sessionId: string }) => {
      await templateApi.deleteSessionTemplate(templateId, sessionId)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateKeys.tableList(TemplateTypeFilter.SESSION) })
      void queryClient.invalidateQueries({ queryKey: templateKeys.infiniteList(TemplateTypeFilter.SESSION) })
    },
  })
}

/**
 * Invalidate template element query
 * Invalides the template element query for a given template id.
 */
export function useInvalidateTemplate(id: string) {
  const queryClient = useQueryClient()
  const invalidateQueryTemplate = () => {
    void queryClient.invalidateQueries({ queryKey: templateKeys.element(id) })
  }
  return {
    invalidateQueryTemplate,
  }
}
